// File: frontend/src/pages/KnowledgeGraph/components/EntityDetailPanel.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Collapse,
  Skeleton,
  alpha,
  Link,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import {
  KGSearchResult,
  KGTimelineResponse,
  KGNode,
  ENTITY_TYPE_COLORS,
} from '../types';
import { cyber } from '../../../theme';

interface EntityDetailPanelProps {
  entity: KGSearchResult | null;
  timeline: KGTimelineResponse | null;
  timelineLoading: boolean;
  relatedNodes: KGNode[];
  connectionCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onCenterEntity: (id: string) => void;
  onSelectRelated: (id: string) => void;
}

const EntityDetailPanel: React.FC<EntityDetailPanelProps> = ({
  entity,
  timeline,
  timelineLoading,
  relatedNodes,
  connectionCount,
  collapsed,
  onToggleCollapse,
  onCenterEntity,
  onSelectRelated,
}) => {
  const [aliasesExpanded, setAliasesExpanded] = React.useState(false);
  const navigate = useNavigate();

  // Cluster nodes may lack report_count (older backend): fall back to the
  // number of distinct reports in the timeline once it loads.
  const reportCount = entity?.report_count || timeline?.appearances.length || 0;
  const reportCountPending = !entity?.report_count && timelineLoading;

  if (!entity) {
    return (
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onToggleCollapse}
          sx={{ position: 'absolute', top: 0, left: -20, zIndex: 10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 20, height: 40 }}
          size="small"
        >
          {collapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      {/* Collapse toggle */}
      <IconButton
        onClick={onToggleCollapse}
        sx={{
          position: 'absolute',
          top: 8,
          left: -20,
          zIndex: 10,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          width: 20,
          height: 40,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        size="small"
      >
        {collapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>

      <Collapse in={!collapsed} orientation="horizontal" sx={{ height: '100%' }}>
        <Paper
          sx={{
            p: 2,
            height: '100%',
            overflow: 'auto',
            minWidth: 280,
            maxWidth: 360,
            borderRadius: 2,
            border: `1px solid ${cyber.border}`,
            boxShadow: 'none',
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, fontFamily: cyber.fontMono }}>
              {entity.name}
            </Typography>
            <Button
              size="small"
              startIcon={<AccountBoxIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/entity/${entity.id}`)}
              sx={{ textTransform: 'none', flexShrink: 0, fontSize: '0.72rem' }}
            >
              Profile
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Chip
              label={entity.type}
              size="small"
              sx={{
                bgcolor: ENTITY_TYPE_COLORS[entity.type] || '#6b7280',
                color: '#fff',
                fontWeight: 600,
              }}
            />
            {entity.external_ids?.map((eid) => (
              <Chip
                key={eid}
                label={eid}
                size="small"
                variant="outlined"
                sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan, borderColor: alpha(cyber.accent.cyan, 0.4) }}
              />
            ))}
          </Stack>

          {/* Aliases */}
          {entity.aliases && entity.aliases.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: 'pointer', mb: 0.5 }}
                onClick={() => setAliasesExpanded(!aliasesExpanded)}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Aliases ({entity.aliases.length})
                </Typography>
                {entity.aliases.length > 3 &&
                  (aliasesExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />)}
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {(aliasesExpanded ? entity.aliases : entity.aliases.slice(0, 3)).map((alias) => (
                  <Chip key={alias} label={alias} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                ))}
                {!aliasesExpanded && entity.aliases.length > 3 && (
                  <Chip label={`+${entity.aliases.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                )}
              </Stack>
            </Box>
          )}

          {/* Stats */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box>
              {reportCountPending ? (
                <Skeleton variant="text" width={32} sx={{ fontSize: '1.25rem' }} />
              ) : (
                <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>
                  {reportCount}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Reports
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>
                {connectionCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Connections
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Timeline */}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Timeline
          </Typography>
          {timelineLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={50} />
              ))}
            </Stack>
          ) : timeline && timeline.appearances.length > 0 ? (
            <Box sx={{ position: 'relative', pl: 2.5, mb: 2 }}>
              {/* Vertical line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 6,
                  top: 4,
                  bottom: 4,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />
              {timeline.appearances.map((app, idx) => (
                <Box key={idx} sx={{ position: 'relative', mb: 2 }}>
                  {/* Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -19,
                      top: 6,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      border: '2px solid',
                      borderColor: 'background.paper',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(app.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => navigate(`/report/${app.report_id}`)}
                    sx={{ display: 'block', fontWeight: 500, lineHeight: 1.3, textAlign: 'left', cursor: 'pointer' }}
                  >
                    {app.title}
                  </Link>
                  <Typography variant="caption" color="text.secondary">
                    {app.source} · "{app.surface_form}"
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              No timeline data available
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Related entities */}
          {relatedNodes.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Related Entities
              </Typography>
              {Object.entries(
                relatedNodes.reduce<Record<string, KGNode[]>>((acc, node) => {
                  if (!acc[node.type]) acc[node.type] = [];
                  acc[node.type].push(node);
                  return acc;
                }, {})
              ).map(([type, nodes]) => (
                <Box key={type} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                    {type} ({nodes.length})
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {nodes.map((node) => (
                      <Chip
                        key={node.id}
                        label={node.name}
                        size="small"
                        onClick={() => onSelectRelated(node.id)}
                        sx={{
                          cursor: 'pointer',
                          borderColor: ENTITY_TYPE_COLORS[node.type],
                          fontSize: '0.7rem',
                          '&:hover': {
                            bgcolor: (theme) => alpha(ENTITY_TYPE_COLORS[node.type] || '#6b7280', 0.15),
                          },
                        }}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Stack spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CenterFocusStrongIcon />}
              onClick={() => onCenterEntity(entity.id)}
              fullWidth
            >
              Center Graph on This Entity
            </Button>
            {timeline && timeline.appearances.length > 0 ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/report/${timeline.appearances[0].report_id}`)}
                fullWidth
              >
                View Reports ({timeline.appearances.length})
              </Button>
            ) : (
              <Tooltip title="No reports available">
                <span>
                  <Button variant="outlined" size="small" startIcon={<VisibilityIcon />} disabled fullWidth>
                    View All Reports
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default EntityDetailPanel;
